import React, { useState, useEffect } from 'react'
import { HiMail, HiClock, HiCheckCircle, HiXCircle } from 'react-icons/hi'
import { Modal, Button } from 'flowbite-react'

const OfflineValidationModal = ({ 
  isOpen, 
  onClose, 
  email, 
  domain, 
  jobId,
  onResumeValidation 
}) => {
  const [checkingStatus, setCheckingStatus] = useState(false)
  const [validationStatus, setValidationStatus] = useState(null) // null, 'completed', 'failed', 'processing'

  const checkValidationStatus = async () => {
    if (!jobId) return
    
    setCheckingStatus(true)
    try {
      const response = await fetch(`https://api.cityseecityfix.com/api/v1/leads/${jobId}/status`)
      if (response.ok) {
        const data = await response.json()
        setValidationStatus(data.status)
        
        if (data.status === 'completed') {
          // Validation is complete, resume the process
          onResumeValidation?.(data.result)
        } else if (data.status === 'failed') {
          // Validation failed
          setValidationStatus('failed')
        } else {
          // Still processing
          setValidationStatus('processing')
        }
      }
    } catch (error) {
      console.error('Error checking validation status:', error)
      setValidationStatus('failed')
    } finally {
      setCheckingStatus(false)
    }
  }

  useEffect(() => {
    if (isOpen && jobId) {
      checkValidationStatus()
    }
    // checkValidationStatus reads only isOpen/jobId, both listed. It is
    // recreated every render, so listing it would re-poll on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, jobId])

  const getStatusIcon = () => {
    switch (validationStatus) {
      case 'completed':
        return <HiCheckCircle className="h-12 w-12 text-green-500" />
      case 'failed':
        return <HiXCircle className="h-12 w-12 text-red-500" />
      case 'processing':
        return <HiClock className="h-12 w-12 text-blue-500" />
      default:
        return <HiMail className="h-12 w-12 text-gray-500" />
    }
  }

  const getStatusMessage = () => {
    switch (validationStatus) {
      case 'completed':
        return {
          title: 'Validation Complete!',
          message: 'Your domain validation has been completed successfully. You can now continue with the registration process.',
          buttonText: 'Continue Registration',
          buttonColor: 'blue'
        }
      case 'failed':
        return {
          title: 'Validation Failed',
          message: 'We encountered an issue validating your domain. Please try again or contact support.',
          buttonText: 'Try Again',
          buttonColor: 'red'
        }
      case 'processing':
        return {
          title: 'Still Processing',
          message: 'Your domain validation is still in progress. This can take a few minutes.',
          buttonText: 'Check Again',
          buttonColor: 'blue'
        }
      default:
        return {
          title: 'Check Your Email',
          message: `We've sent you an email at ${email} with a link to continue the validation process.`,
          buttonText: 'Check Status',
          buttonColor: 'blue'
        }
    }
  }

  const handleButtonClick = () => {
    if (validationStatus === 'completed') {
      onResumeValidation?.(validationStatus)
    } else if (validationStatus === 'processing') {
      checkValidationStatus()
    } else {
      checkValidationStatus()
    }
  }

  const statusInfo = getStatusMessage()

  return (
    <Modal show={isOpen} onClose={onClose} size="md">
      <Modal.Header>
        <div className="flex items-center space-x-3">
          {getStatusIcon()}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              {statusInfo.title}
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Domain: {domain}
            </p>
          </div>
        </div>
      </Modal.Header>
      
      <Modal.Body>
        <div className="text-center space-y-4">
          <p className="text-gray-700 dark:text-gray-300">
            {statusInfo.message}
          </p>
          
          {validationStatus === 'processing' && (
            <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
              <div className="flex items-center justify-center space-x-2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
                <span className="text-sm text-blue-700 dark:text-blue-300">
                  Processing in background...
                </span>
              </div>
            </div>
          )}
          
          {validationStatus === null && (
            <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">
              <div className="flex items-center justify-center space-x-2">
                <HiMail className="h-5 w-5 text-gray-500" />
                <span className="text-sm text-gray-700 dark:text-gray-300">
                  Check your email for the continuation link
                </span>
              </div>
            </div>
          )}
        </div>
      </Modal.Body>
      
      <Modal.Footer>
        <div className="flex space-x-3 w-full">
          <Button 
            color="gray" 
            onClick={onClose}
            className="flex-1"
          >
            Close
          </Button>
          <Button 
            color={statusInfo.buttonColor}
            onClick={handleButtonClick}
            disabled={checkingStatus}
            className="flex-1"
          >
            {checkingStatus ? (
              <div className="flex items-center space-x-2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                <span>Checking...</span>
              </div>
            ) : (
              statusInfo.buttonText
            )}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  )
}

export default OfflineValidationModal
